/**
 * AceitarConvite — página pública para aceitar convite de cargo administrativo.
 *
 * Fluxo:
 *   1. Carrega convite via token (GET /admin/invitations/by-token/:token)
 *   2. Mostra detalhes (cargo, quem convidou, email)
 *   3. Usuário define senha (e opcionalmente telefone/CPF)
 *   4. POST /admin/invitations/:token/accept → login automático no admin
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Lock, Phone, IdCard, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { adminInvitationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PasswordStrength, { isPasswordStrong } from '../components/PasswordStrength';
import { formatCpf, validateCpf, stripCpf } from '../utils/cpf';

export default function AceitarConvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    password: '',
    password_confirm: '',
    telefone: '',
    cpf: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminInvitationsApi.getByToken(token);
        if (!cancelled) {
          setInvitation(data);
          setForm((f) => ({
            ...f,
            telefone: data.telefone || '',
          }));
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail || 'Convite inválido ou expirado.';
          setError(detail);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const validate = () => {
    const e = {};
    if (!form.password) e.password = 'Senha obrigatória';
    else if (!isPasswordStrong(form.password)) e.password = 'Sua senha não atende aos requisitos abaixo';
    if (!form.password_confirm) e.password_confirm = 'Confirme a senha';
    else if (form.password !== form.password_confirm) e.password_confirm = 'As senhas não conferem';
    if (form.cpf && !validateCpf(form.cpf)) e.cpf = 'CPF inválido';
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        password: form.password,
      };
      if (form.telefone) payload.telefone = form.telefone;
      if (form.cpf) payload.cpf = stripCpf(form.cpf);
      const res = await adminInvitationsApi.accept(token, payload);
      login(res.access_token, res.user, res.refresh_token);
      toast.success(`Convite aceito! Bem-vindo(a), ${res.user.nome}.`);
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível aceitar o convite.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="invite-error">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Convite indisponível</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-purple-600 hover:bg-purple-700">
            Ir para o início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 px-4 py-12"
      data-testid="aceitar-convite-page"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Convite Administrativo</h1>
          <p className="text-sm text-gray-600">
            Você foi convidado(a) para o cargo de <strong className="text-purple-700">{invitation.role_label}</strong>
            {invitation.invited_by_name && (
              <> por <strong>{invitation.invited_by_name}</strong></>
            )}.
          </p>
        </div>

        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-6 text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Email</span>
            <strong className="text-gray-900">{invitation.email}</strong>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Cargo</span>
            <strong className="text-purple-700">{invitation.role_label}</strong>
          </div>
          {invitation.municipio_label && (
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">Município</span>
              <strong className="text-gray-900">{invitation.municipio_label}</strong>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Crie sua senha <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Senha forte"
                className={`pl-10 ${formErrors.password ? 'border-red-400' : ''}`}
                data-testid="invite-password-input"
              />
            </div>
            {formErrors.password && <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>}
            <PasswordStrength password={form.password} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirme a senha <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="password"
                value={form.password_confirm}
                onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                placeholder="Repita a senha"
                className={`pl-10 ${formErrors.password_confirm ? 'border-red-400' : ''}`}
                data-testid="invite-password-confirm-input"
              />
            </div>
            {formErrors.password_confirm && <p className="text-xs text-red-500 mt-1">{formErrors.password_confirm}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                placeholder="+5531999998888"
                className="pl-10"
                data-testid="invite-telefone-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
            <div className="relative">
              <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: formatCpf(e.target.value) })}
                placeholder="000.000.000-00"
                inputMode="numeric"
                maxLength={14}
                className={`pl-10 ${formErrors.cpf ? 'border-red-400' : ''}`}
                data-testid="invite-cpf-input"
              />
            </div>
            {formErrors.cpf && <p className="text-xs text-red-500 mt-1">{formErrors.cpf}</p>}
          </div>

          <Button
            type="submit"
            className="w-full bg-purple-600 hover:bg-purple-700 h-11"
            disabled={submitting}
            data-testid="invite-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aceitando...</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 mr-2" /> Aceitar convite</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
